import * as THREE from "three";
import {
  WallConnectionEnd,
  WallConnectionStart,
  WallType,
} from "../../types/floorplannerTypes";
import {
  getRoomShapeDefinition,
  RoomShapeId,
  RoomShapeParams,
  verticesMmToWorldCentered,
} from "./roomShapeTemplates";

export interface BuiltRoomWalls {
  walls: WallType[];
  /** Wall ids in polygon order (same as vertices). */
  wallIds: string[];
}

/**
 * Build closed-loop wall objects (centerline coincident corners + centerConnections)
 * from a room shape template and parameter set.
 */
export function buildRoomWallsFromTemplate(options: {
  shapeId: RoomShapeId;
  params: RoomShapeParams;
  generateId: () => string;
  wallWidth?: number;
  /** Wall fill color as 0xRRGGBB */
  fillColor?: number;
}): BuiltRoomWalls {
  const {
    shapeId,
    params,
    generateId,
    wallWidth = 0.1,
    fillColor,
  } = options;
  const definition = getRoomShapeDefinition(shapeId);
  const verticesMm = definition.getVertices(params);
  const verticesWorld = verticesMmToWorldCentered(verticesMm);

  const walls: WallType[] = [];
  const wallIds: string[] = [];

  for (let i = 0; i < verticesWorld.length; i++) {
    const start = verticesWorld[i];
    const end = verticesWorld[(i + 1) % verticesWorld.length];
    const id = generateId();
    wallIds.push(id);
    walls.push({
      id,
      type: "wall",
      start: new THREE.Vector2(start.x, start.y),
      end: new THREE.Vector2(end.x, end.y),
      wallWidth,
      lineForm: "straight",
      lineType: "solid",
      lineWeight: 0.01,
      ...(fillColor !== undefined ? { fillColor } : {}),
      centerConnections: [],
      edgeConnections: [],
      cutouts: [],
    });
  }

  // Link consecutive walls into a closed loop via center connections.
  for (let i = 0; i < walls.length; i++) {
    const current = walls[i];
    const next = walls[(i + 1) % walls.length];
    current.centerConnections = current.centerConnections ?? [];
    next.centerConnections = next.centerConnections ?? [];
    current.centerConnections.push({
      id: next.id,
      sourcePosition: WallConnectionEnd,
      targetPosition: WallConnectionStart,
    });
    next.centerConnections.push({
      id: current.id,
      sourcePosition: WallConnectionStart,
      targetPosition: WallConnectionEnd,
    });
  }

  return { walls, wallIds };
}
